import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Order } from '../../interfaces/orders/order';
import { UltimateBeneficialOwnershipStructure } from '../../interfaces/ultimate-beneficial-ownership-structure/ultimate-beneficial-ownership-structure';
import { eBusinessSearchCriteriaTypeString, eOrderType, ePartyType } from '../../enums';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { Router } from '@angular/router';
import { Utilities } from '../../utilities';
import { UboTreeNode } from '../../interfaces/hub-reports/ubo-tree-node';
import { HubReportsService } from '../../services/hub-reports/hub-reports.service';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-ubo-beneficial-owners-review',
    templateUrl: './ubo-beneficial-owners-review.component.html',
    styleUrls: [
        './ubo-beneficial-owners-review.component.css',
        './../../../results-page.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UboBeneficialOwnersReviewComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() public order!: Order;
  @Input() admin: boolean = false;

  displayedColumns = [
    'name',
    'address',
    'ownershipLevel',
    'ownershipPercentageOfParent',
    'ownershipPercentageOfRoot'
  ];

  dataSource = new MatTableDataSource<UboTreeNode>([]);
  isLoadingOwnershipStructure = false;

  protected hasDeclaredDoNotKnowAnyShareholders: boolean = false;
  protected hasDeclaredDoNotKnowMoreShareholders: boolean = false;
  protected hasDeclaredNoSignificantShareholders: boolean = false;
  protected hasDeclaredNoOtherSignificantShareholders: boolean = false;

  protected rootEntityName: string = this.transloco.translate('uboBeneficialOwnersReview.root');

  private onDestroy$ = new Subject<void>();

  constructor(
    private ordersRepo: OrderRepositoryService,
    private router: Router,
    private hubReportService: HubReportsService) { }

  ngOnInit(): void {
    if (!this.order.ultimateBeneficialOwnershipRequest?.id) return;
    this.isLoadingOwnershipStructure = true;

    this.hubReportService.currentHubReport$.pipe(
        takeUntil(this.onDestroy$),
        switchMap(report => !report ? this.hubReportService.getHubReportWithUboTreesByOrder(this.order.id, true) : of(report))
      ).subscribe(report => {
        const root = report?.uboTrees.at(0);

        if (!root) {
          return;
        }

        if (root.party.busName) {
          this.rootEntityName = root.party.busName;
        }

        const findFirstNodeCreatedByThisOrder = (node: UboTreeNode): UboTreeNode | null => {
          let found: UboTreeNode | null = null;

          if (node.createdForOrderID === this.order.id) {
            found = node;
          } else {
            for (let child of node.children) {
              found = findFirstNodeCreatedByThisOrder(child);

              if (found) {
                break;
              }
            }
          }

          return found;
        }

        const node = findFirstNodeCreatedByThisOrder(root);

        if (node) {
          this.hasDeclaredDoNotKnowAnyShareholders = node.hasDeclaredDoNotKnowAnyShareholders ?? false;
          this.hasDeclaredDoNotKnowMoreShareholders = node.hasDeclaredDoNotKnowMoreShareholders ?? false;
          this.hasDeclaredNoSignificantShareholders = node.hasDeclaredNoSignificantShareholders ?? false;
          this.hasDeclaredNoOtherSignificantShareholders = node.hasDeclaredNoOtherSignificantShareholders ?? false;

          const nodes: UboTreeNode[] = [];

          const getOrderShareholders = (node: UboTreeNode) => {
            for (let child of node.children) {
              nodes.push(child);
              getOrderShareholders(child);
            }
          };

          getOrderShareholders(node);

          this.dataSource = new MatTableDataSource(nodes
            .filter(n => !!n.parentID)
            .sort((a, b) => (b.ownershipPercentageOfRoot ?? 0) - (a.ownershipPercentageOfRoot ?? 0))
            .sort((a, b) => (a.ownershipLevel ?? 0) - (b.ownershipLevel ?? 0)) ?? []);

          let index = 2;

          if (nodes.some(n => n.businessCountry) &&
            !this.displayedColumns.includes('businessCountry')) {
            this.displayedColumns.splice(index++, 0, 'businessCountry');
          }

          if (nodes.some(n => n.businessJurisdiction) &&
            !this.displayedColumns.includes('businessJurisdiction')) {
            this.displayedColumns.splice(index++, 0, 'businessJurisdiction');
          }

          if (nodes.some(n => n.party.busNumber) &&
            !this.displayedColumns.includes('businessNumber')) {
            this.displayedColumns.splice(index++, 0, 'businessNumber');
          }

          if (nodes.some(n => n.party.partyTypeID === ePartyType.businessShareholder) &&
            !this.displayedColumns.includes('isValidated')) {
            this.displayedColumns.splice(index++, 0, 'isValidated');
          }

          if (nodes.some(n => n.party.dateOfBirth) &&
            !this.displayedColumns.includes('dateOfBirth')) {
            this.displayedColumns.splice(index++, 0, 'dateOfBirth');
          }

          if (nodes.some(n => n.party.partyTypeID === ePartyType.businessShareholder)) {
            this.displayedColumns.unshift('icon');
            this.displayedColumns.push('actions');
          }
        }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.hubReportService.currentHubReport$.next(undefined);
  }

  formatName(structure: UltimateBeneficialOwnershipStructure) {
    var name: string | undefined = "";
    if (structure.party.partyTypeID == ePartyType.businessBeneficialOwner || structure.party.partyTypeID === ePartyType.businessShareholder) {
      name = structure.party.busName!;
    } else {
      name = (structure.party.middleName)
        ? structure.party.firstName + " " + structure.party.middleName + " " + structure.party.lastName
        : structure.party.firstName + " " + structure.party.lastName;
    }

    return name?.trim();
  }

  formatAddress(structure: UltimateBeneficialOwnershipStructure) {
    return Utilities.formatAddress(structure.party?.contactDetails?.address);
  }

  orderBusinessSearch(shareholder: UltimateBeneficialOwnershipStructure) {
    const order = {
      referenceNumber: this.order.referenceNumber,
      orderTypeID: eOrderType.businessSearch,
      orderGroupID: this.order.orderGroupID,
      country: this.order.country,
      jurisdiction: '_M',
      businessSearchCriteria: {
        name: shareholder.party.busName,
        businessSearchCriteriaTypeID: eBusinessSearchCriteriaTypeString.Name
      },
      searchParameters: {
        searchJurisdictions: [
          { jurisdiction: 'BC' },
          { jurisdiction: 'AB' },
          { jurisdiction: 'SK' },
          { jurisdiction: 'MB' },
          { jurisdiction: 'ON' },
          { jurisdiction: 'QC' },
          { jurisdiction: '_F' },
          { jurisdiction: 'YT' },
          { jurisdiction: 'NT' },
          { jurisdiction: 'NU' },
          { jurisdiction: 'NL' },
          { jurisdiction: 'NB' },
          { jurisdiction: 'NS' },
          { jurisdiction: 'PE' },
        ]
      }
    };

    this.ordersRepo.createNewOrder(order).subscribe(result => {
      if (result.resource.id) {
        this.router.navigate(['orders', 'business', result.resource.id]);
      }
    });
  }

  orderBusinessReport(shareholder: UltimateBeneficialOwnershipStructure) {
    const order = {
      referenceNumber: this.order.referenceNumber,
      originalOrderID: this.order.id,
      orderTypeID: eOrderType.businessReport,
      country: this.order.country,
      jurisdiction: shareholder.businessJurisdiction!,
      businessReportCriteria: {
        number: shareholder.party.busNumber,
        businessName: shareholder.party.busName
      }
    };

    this.ordersRepo.createNewOrder(order).subscribe(result => {
      if (result.resource.id) {
        this.router.navigate(['orders', 'business', result.resource.id]);
      }
    });
  }

  protected getBusinessJurisdiction(jurisdiction: string | null | undefined) {
    if (!jurisdiction) {
      return '—';
    } else if (jurisdiction === '_F') {
      return 'FED';
    } else {
      return jurisdiction;
    }
  }
}